import type { SeedingGroup, SeedingItem } from './group-seeding';

/**
 * Обложки посевов: картинка ролика над комментариями, как Артур кладёт её в
 * отчёт руками («нужно чтобы он делал скрин видоса… скрин 1 кадра», 30.09).
 *
 * Ролик во всех пабликах посева один и тот же, поэтому картинка на блок одна —
 * с самой просматриваемой копии. Адрес CDN Instagram подписан и живёт около
 * суток, а браузер с CDN картинку не скачает (нет CORS), поэтому её
 * перезаливает бэкенд — та же ручка /api/reports/image-urls, что у превью
 * отчёта по аккаунту, с общим кэшем по id публикации.
 *
 * Копия не перезалилась — пробуем следующую. Не вышло ни с одной — блок идёт
 * без картинки: отчёт без обложки лучше, чем отчёта нет.
 */

export type AttachCoversDeps = {
  /** id публикации → постоянный адрес; кого нет в ответе, тот не перезалился. */
  fetchImageUrls: (items: Array<{ id: string; thumbnailUrl: string }>) => Promise<Map<string, string>>;
};

/** Сколько копий одного посева пробовать, прежде чем сдаться. */
const MAX_TRIES = 3;

/** Копии с картинкой, самая просматриваемая первой. */
export function coverCandidates(group: SeedingGroup): SeedingItem[] {
  return group.items
    .filter((item) => item.coverSource && item.mediaId)
    .sort((a, b) => (b.views ?? 0) - (a.views ?? 0))
    .slice(0, MAX_TRIES);
}

export async function attachSeedingCovers(
  groups: SeedingGroup[],
  deps: AttachCoversDeps,
  onProgress: (message: string) => void = () => {},
): Promise<SeedingGroup[]> {
  const candidates = groups.map(coverCandidates);
  const covers: Array<string | null> = groups.map(() => null);

  for (let round = 0; round < MAX_TRIES; round++) {
    const wanted = candidates
      .map((list, index) => ({ index, item: covers[index] ? undefined : list[round] }))
      .filter((w): w is { index: number; item: SeedingItem } => Boolean(w.item));
    if (wanted.length === 0) break;

    onProgress('Обложки роликов...');
    let urls: Map<string, string>;
    try {
      urls = await deps.fetchImageUrls(
        wanted.map(({ item }) => ({ id: item.mediaId, thumbnailUrl: item.coverSource as string })),
      );
    } catch (err) {
      // Бэкенд не ответил — следующая копия упрётся в него же.
      console.warn('[Отработано] обложки не перезалились:', err);
      break;
    }
    for (const { index, item } of wanted) covers[index] = urls.get(item.mediaId) || null;
  }

  return groups.map((group, index) => ({ ...group, cover: covers[index] }));
}
